import { Box } from "@mui/material"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"

import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { s } from "../../../../utilities/common"
import { theme } from "../../../../utilities/theme"
import { useProgress } from "../../use-progress"

export const ProgressLeftButton = ({
  focusKey,
  type = "chapter",
}: {
  focusKey?: string
  type: "chapter" | "title" | "progress"
}) => {
  const t = useTranslate(Namespaces.Common)
  const { changeChapter } = useProgress()
  const { focused, focusSelf, ref } = useFocusable({
    focusKey: focusKey,
    onEnterPress: () => changeChapter("left"),
  })

  return (
    <Box
      tabIndex={0}
      aria-label={t("player__button_backward")}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={() => changeChapter("left")}
      ref={ref}
    >
      {type === "chapter" ? (
        <svg
          width={s("72px")}
          height={s("72px")}
          viewBox="32 24 72 72"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="68"
            cy="60"
            r="36"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M64.1182 72.2012C63.4883 72.2012 62.9463 72.0254 62.3311 71.6592L48.1074 63.3096C47.0527 62.6943 46.4814 61.9326 46.4814 60.9512C46.4814 59.9697 47.0381 59.2373 48.1074 58.6074L62.3311 50.2578C62.9609 49.8916 63.4883 49.7158 64.1182 49.7158C65.3779 49.7158 66.4326 50.668 66.4326 52.4111V60.3799C66.623 59.6768 67.1357 59.1055 68 58.6074L82.2236 50.2578C82.8389 49.8916 83.3809 49.7158 84.0107 49.7158C85.2705 49.7158 86.3105 50.668 86.3105 52.4111V69.5205C86.3105 71.249 85.2559 72.2012 83.9961 72.2012C83.3662 72.2012 82.8242 72.0254 82.2236 71.6592L68 63.3096C67.1504 62.8262 66.623 62.2402 66.4326 61.5225V69.5205C66.4326 71.249 65.3779 72.2012 64.1182 72.2012Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        </svg>
      ) : type === "title" ? (
        <svg
          width={s("72px")}
          height={s("72px")}
          viewBox="32 24 72 72"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="68"
            cy="60"
            r="36"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M56.4131 72.6113C55.0508 72.6113 54.3477 71.9082 54.3623 70.5605V51.4443C54.3623 50.082 55.0654 49.3936 56.4131 49.3936H59.6943C61.042 49.4082 61.7305 50.0674 61.7305 51.4443V60.4092C61.9209 59.7061 62.4482 59.1494 63.2979 58.6367L77.5215 50.2871C78.1367 49.9355 78.6787 49.7598 79.3086 49.7598C80.5684 49.7598 81.623 50.6973 81.623 52.4404V69.5498C81.623 71.293 80.5537 72.2451 79.2939 72.2451C78.6787 72.2451 78.1367 72.0547 77.5215 71.7031L63.2979 63.3535C62.4482 62.8555 61.9209 62.2695 61.7305 61.5518V70.5605C61.7305 71.9082 61.042 72.6113 59.6943 72.6113H56.4131Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        </svg>
      ) : (
        <svg
          width={s("72")}
          height={s("72")}
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="32"
            height="32"
            rx="16"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M16.0042 10.9C18.8231 10.9 21.1084 13.1852 21.1084 16.0042C21.1084 18.8231 18.8231 21.1084 16.0042 21.1084C13.1852 21.1084 10.9 18.8231 10.9 16.0042C10.9 14.2812 11.7537 12.7576 13.0612 11.8334"
            stroke={focused ? theme.palette.new.playerButton : "white"}
            stroke-width="1.3"
            strokeLinecap="round"
          />
          <path
            d="M16.6693 9.66358L16.6693 10.4432L16.6693 11.6126L16.6693 12.3923C16.6815 12.7699 16.243 12.9404 15.975 12.6724L14.6107 11.3081C14.4523 11.1497 14.4523 10.9061 14.6228 10.7356L15.9872 9.37123C16.243 9.11541 16.6815 9.28595 16.6693 9.66358Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
          <path
            d="M15.4709 13.9746C15.6022 13.9746 15.7228 14.065 15.7639 14.1855L15.7795 14.2324C15.8022 14.3262 15.7802 14.4263 15.718 14.5029L15.6838 14.5391L14.512 15.6123H14.6526C15.3544 15.6123 15.9424 16.1901 15.9426 16.9014C15.9426 17.6128 15.3545 18.1914 14.6526 18.1914H13.8997C13.4616 18.1914 13.0706 17.9156 12.9368 17.4961L12.9211 17.4316C12.9004 17.2817 12.9879 17.1366 13.1379 17.0918L13.2024 17.0762C13.3528 17.0554 13.4982 17.1439 13.551 17.293V17.2939L13.5745 17.3496C13.6382 17.4729 13.7629 17.5467 13.9084 17.5469H14.6526C15.0017 17.5469 15.2971 17.2594 15.2971 16.9014C15.2969 16.5526 15.0014 16.2568 14.6526 16.2568H13.6858C13.5464 16.2568 13.4241 16.1769 13.3831 16.0537V16.0527C13.3341 15.9321 13.3641 15.7923 13.4641 15.7021L14.636 14.6201H13.2395C13.0584 14.6201 12.9164 14.479 12.9163 14.2979C12.9163 14.1264 13.0578 13.9746 13.2395 13.9746H15.4709ZM17.8391 13.9844C18.671 13.9845 19.3518 14.6559 19.3518 15.4883V16.6787C19.3517 17.5108 18.6712 18.1913 17.8391 18.1914C16.9975 18.1914 16.3255 17.5108 16.3254 16.6787V15.4971C16.3256 14.6557 16.9977 13.9844 17.8391 13.9844ZM17.8391 14.6201C17.3602 14.6201 16.9709 15.0183 16.9709 15.4883V16.6787C16.971 17.1578 17.36 17.5469 17.8391 17.5469C18.3089 17.5467 18.7062 17.1575 18.7063 16.6787V15.4883C18.7063 15.0185 18.3088 14.6203 17.8391 14.6201Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
            stroke={focused ? theme.palette.new.playerButton : "white"}
            stroke-width="0.05"
          />
        </svg>
      )}
    </Box>
  )
}
